/**
 * Foundry-flavoured fixtures for the Tier-2 / pack routes.
 *
 * These are curated subsets that ship with the app bundle so demo-mode
 * tenants can navigate without a backend. Once the backend's `/v1/twin/*`
 * endpoints land, route components swap these for React Query calls.
 *
 * Distributions + planted correlations mirror packs/foundry/demo-data/.
 */

import type { CorrelationCell } from "@foundry/CorrelationHeatmap";
import type { BatchTraceData } from "@foundry/BatchTraceView";
import type { DefectCount } from "@foundry/DefectParetoChart";
import type { CastingHoldBadge } from "@/lib/api/casting-holds";

// --- Correlation matrix ------------------------------------------------

export const FX_CORRELATION_PARAMS = [
  "moisture_content",
  "melt_temperature",
  "pour_temperature",
  "carbon",
  "squeeze_pressure",
  "hold_time",
  "superheat",
  "compactibility",
  "rejection_rate",
];

export const FX_CORRELATION_LABELS: Record<string, string> = {
  moisture_content: "Sand moisture",
  melt_temperature: "Melt temp",
  pour_temperature: "Pour temp",
  carbon: "Carbon",
  squeeze_pressure: "Squeeze pressure",
  hold_time: "Hold time",
  superheat: "Superheat",
  compactibility: "Compactibility",
  rejection_rate: "Rejection rate",
};

/**
 * Correlation matrix synthesized from packs/foundry/demo-data/correlations.json
 * Pearson r values (rounded to 3 dp) consistent with the planted patterns.
 */
export const FX_CORRELATION_CELLS: CorrelationCell[] = [
  // Strong planted correlations
  { paramA: "moisture_content", paramB: "rejection_rate", r: 0.377, pValue: 0.0001, n: 500 },
  { paramA: "carbon", paramB: "rejection_rate", r: -0.393, pValue: 0.0001, n: 500 },
  { paramA: "pour_temperature", paramB: "rejection_rate", r: -0.191, pValue: 0.001, n: 500 },
  { paramA: "squeeze_pressure", paramB: "rejection_rate", r: 0.046, pValue: 0.31, n: 500 },
  // Plausible secondary correlations
  { paramA: "compactibility", paramB: "rejection_rate", r: 0.102, pValue: 0.022, n: 500 },
  { paramA: "moisture_content", paramB: "compactibility", r: 0.421, pValue: 0.0001, n: 500 },
  { paramA: "moisture_content", paramB: "squeeze_pressure", r: -0.063, pValue: 0.16, n: 500 },
  { paramA: "melt_temperature", paramB: "superheat", r: 0.512, pValue: 0.0001, n: 500 },
  { paramA: "melt_temperature", paramB: "pour_temperature", r: 0.298, pValue: 0.0001, n: 500 },
  { paramA: "melt_temperature", paramB: "rejection_rate", r: 0.007, pValue: 0.88, n: 500 },
  { paramA: "hold_time", paramB: "rejection_rate", r: 0.006, pValue: 0.89, n: 500 },
  { paramA: "superheat", paramB: "rejection_rate", r: -0.007, pValue: 0.88, n: 500 },
  { paramA: "carbon", paramB: "pour_temperature", r: 0.041, pValue: 0.37, n: 500 },
  { paramA: "hold_time", paramB: "superheat", r: 0.388, pValue: 0.0001, n: 500 },
  { paramA: "compactibility", paramB: "squeeze_pressure", r: 0.21, pValue: 0.001, n: 500 },
];

export interface CorrelationInsight {
  paramA: string;
  paramB: string;
  r: number;
  pValue: number;
  insight: string;
  /** Provenance of the insight (C2): "domain_guidance" = a CURATED foundry
   *  mechanism (a known relationship, contextualised with this org's live r/p),
   *  "derived" = a strong pair actually FOUND in this org's data. Lets the card
   *  distinguish taught guidance from discovered correlation. */
  source?: string;
}

export const FX_CORRELATION_INSIGHTS: CorrelationInsight[] = [
  {
    paramA: "moisture_content",
    paramB: "rejection_rate",
    r: 0.377,
    pValue: 0.0001,
    insight:
      "Elevated sand moisture (>4.2%) produces steam at pour, forming porosity in the casting. Tighten the moisture window during humid shifts.",
  },
  {
    paramA: "carbon",
    paramB: "rejection_rate",
    r: -0.393,
    pValue: 0.0001,
    insight:
      "Carbon outside the 3.0–3.3% target — particularly on the low end — drives mechanical-test failures. Verify charge ratios + spectrometer calibration.",
  },
  {
    paramA: "pour_temperature",
    paramB: "rejection_rate",
    r: -0.191,
    pValue: 0.001,
    insight:
      "Pour temperatures below 1360 °C correlate with misruns and cold shuts. Hold-time tuning before tap may help during slow-pour shifts.",
  },
  {
    paramA: "moisture_content",
    paramB: "compactibility",
    r: 0.421,
    pValue: 0.0001,
    insight:
      "Sand moisture and compactibility move together — expected, but useful as a cross-check on the moisture sensor.",
  },
  {
    paramA: "melt_temperature",
    paramB: "superheat",
    r: 0.512,
    pValue: 0.0001,
    insight:
      "Bath temperature and superheat track tightly. Expected since superheat is derived from melt temp minus liquidus; use this as a sensor sanity check.",
  },
];

// --- Scatter samples ---------------------------------------------------

export interface ScatterSample {
  x: number;
  y: number;
  category: "pass" | "fail";
  label: string;
}

/**
 * Generate a deterministic scatter sample for a given (x-mean, x-std,
 * y-mean, y-std, correlation) configuration. Uses a fixed seed so the
 * scatter doesn't flicker between renders.
 */
function makeScatter(
  xMean: number,
  xStd: number,
  yMean: number,
  yStd: number,
  r: number,
  count = 80,
): ScatterSample[] {
  let seed = 12345;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const samples: ScatterSample[] = [];
  for (let i = 0; i < count; i++) {
    const u1 = Math.max(1e-9, rand());
    const u2 = rand();
    const z1 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const u3 = Math.max(1e-9, rand());
    const u4 = rand();
    const z2 = Math.sqrt(-2 * Math.log(u3)) * Math.cos(2 * Math.PI * u4);
    const x = xMean + xStd * z1;
    const y = yMean + yStd * (r * z1 + Math.sqrt(1 - r * r) * z2);
    const category: "pass" | "fail" = y > yMean + yStd ? "fail" : "pass";
    samples.push({
      x: Math.round(x * 1000) / 1000,
      y: Math.round(y * 1000) / 1000,
      category,
      label: `Heat H-${(4000 + i).toString().padStart(5, "0")}`,
    });
  }
  return samples;
}

export const FX_SCATTER_BY_PAIR: Record<string, ScatterSample[]> = {
  "moisture_content|rejection_rate": makeScatter(3.8, 0.5, 0.13, 0.06, 0.377),
  "carbon|rejection_rate": makeScatter(3.1, 0.08, 0.13, 0.06, -0.393),
  "pour_temperature|rejection_rate": makeScatter(1385, 14, 0.13, 0.06, -0.191),
  "squeeze_pressure|rejection_rate": makeScatter(8.0, 0.6, 0.13, 0.06, 0.046),
  "melt_temperature|superheat": makeScatter(1450, 20, 85, 12, 0.512),
  "moisture_content|compactibility": makeScatter(3.8, 0.5, 42, 3, 0.421),
};

// --- Batch list --------------------------------------------------------

export interface FoundryBatchRow {
  /** The heat's id (twin_batches.id) when it came from the live API; the heat-lots read keys on it. */
  id?: string;
  heatNumber: string;
  grade: string;
  shift: string;
  startedAt: string;
  status: "pass" | "fail";
  castingsTotal: number;
  castingsAccepted: number;
  castingsRejected: number;
  defectType: string | null;
  /**
   * S2.3b (2026-08-03): these three were `number`, and `toBatchRow` coerced a
   * server NULL to `0` to satisfy that — so a heat where nobody measured carbon
   * rendered 0.00 %. `twin_batches.carbon` is NULL on EVERY new-model and captured
   * heat, so the non-nullable type was not describing reality, it was forcing a
   * fabrication. `null` = not measured, and a renderer must show "—" for it.
   */
  pourTemp: number | null;
  moisture: number | null;
  carbon: number | null;
  /** Optional dimensions — present for imported lots, blank for plant-run heats. */
  part?: string;
  line?: string;
  /** Where the heat came from; an imported heat shows its lots' scrap, not a pass or fail (lib/heat-outcome). */
  source?: string;
  /** The casting holds the heat is in, open first (station workspaces, step 6); absent from an older API. */
  castingHolds?: CastingHoldBadge[];
}

export const FX_BATCHES: FoundryBatchRow[] = [
  { heatNumber: "H-04821", grade: "FG260", shift: "B", startedAt: "2026-05-24T10:15:00.000Z", status: "pass", castingsTotal: 102, castingsAccepted: 98, castingsRejected: 4, defectType: null, pourTemp: 1388, moisture: 3.92, carbon: 3.12 },
  { heatNumber: "H-04820", grade: "FG200", shift: "B", startedAt: "2026-05-24T08:45:00.000Z", status: "fail", castingsTotal: 96, castingsAccepted: 70, castingsRejected: 26, defectType: "porosity", pourTemp: 1391, moisture: 4.45, carbon: 3.08 },
  { heatNumber: "H-04819", grade: "FG260", shift: "A", startedAt: "2026-05-24T07:15:00.000Z", status: "pass", castingsTotal: 110, castingsAccepted: 105, castingsRejected: 5, defectType: null, pourTemp: 1384, moisture: 3.78, carbon: 3.15 },
  { heatNumber: "H-04818", grade: "FG260", shift: "A", startedAt: "2026-05-24T05:45:00.000Z", status: "fail", castingsTotal: 88, castingsAccepted: 64, castingsRejected: 24, defectType: "misrun", pourTemp: 1352, moisture: 3.82, carbon: 3.11 },
  { heatNumber: "H-04817", grade: "FG200", shift: "A", startedAt: "2026-05-24T04:15:00.000Z", status: "pass", castingsTotal: 104, castingsAccepted: 99, castingsRejected: 5, defectType: null, pourTemp: 1389, moisture: 3.74, carbon: 3.13 },
  { heatNumber: "H-04816", grade: "FG300", shift: "C", startedAt: "2026-05-24T02:45:00.000Z", status: "pass", castingsTotal: 92, castingsAccepted: 88, castingsRejected: 4, defectType: null, pourTemp: 1392, moisture: 3.81, carbon: 3.18 },
  { heatNumber: "H-04815", grade: "FG200", shift: "C", startedAt: "2026-05-24T01:15:00.000Z", status: "fail", castingsTotal: 100, castingsAccepted: 74, castingsRejected: 26, defectType: "shrinkage", pourTemp: 1380, moisture: 3.84, carbon: 3.06 },
  { heatNumber: "H-04814", grade: "FG260", shift: "C", startedAt: "2026-05-23T23:45:00.000Z", status: "pass", castingsTotal: 108, castingsAccepted: 102, castingsRejected: 6, defectType: null, pourTemp: 1386, moisture: 3.92, carbon: 3.14 },
  { heatNumber: "H-04813", grade: "FG300", shift: "B", startedAt: "2026-05-23T22:15:00.000Z", status: "pass", castingsTotal: 96, castingsAccepted: 92, castingsRejected: 4, defectType: null, pourTemp: 1395, moisture: 3.69, carbon: 3.16 },
  { heatNumber: "H-04812", grade: "FG200", shift: "B", startedAt: "2026-05-23T20:45:00.000Z", status: "fail", castingsTotal: 102, castingsAccepted: 78, castingsRejected: 24, defectType: "porosity", pourTemp: 1387, moisture: 4.38, carbon: 2.92 },
  { heatNumber: "H-04811", grade: "FG260", shift: "B", startedAt: "2026-05-23T19:15:00.000Z", status: "pass", castingsTotal: 100, castingsAccepted: 96, castingsRejected: 4, defectType: null, pourTemp: 1390, moisture: 3.85, carbon: 3.10 },
  { heatNumber: "H-04810", grade: "FG200", shift: "A", startedAt: "2026-05-23T17:45:00.000Z", status: "fail", castingsTotal: 94, castingsAccepted: 70, castingsRejected: 24, defectType: "blow_hole", pourTemp: 1378, moisture: 4.52, carbon: 3.07 },
];

// --- Batch trace fixtures (full lifecycle for the drill-in) ------------

export const FX_BATCH_TRACE_PASS: BatchTraceData = {
  heatNumber: "H-04821",
  grade: "FG260",
  shift: "B",
  startedAt: "2026-05-24T10:15:00.000Z",
  endedAt: "2026-05-24T12:05:00.000Z",
  status: "pass",
  castingsTotal: 102,
  castingsAccepted: 98,
  castingsRejected: 4,
  stages: [
    {
      id: "s-1",
      name: "Charge preparation",
      kind: "charge_prep",
      startedAt: "2026-05-24T10:15:00.000Z",
      endedAt: "2026-05-24T10:25:00.000Z",
      parameters: [
        { name: "charge_weight", value: 2150, unit: "kg", inSpec: true, range: [1800, 2500] },
        { name: "pig_iron_pct", value: 70, unit: "%", inSpec: true, range: [65, 75] },
        { name: "scrap_pct", value: 30, unit: "%", inSpec: true, range: [25, 35] },
      ],
    },
    {
      id: "s-2",
      name: "Sand mixing",
      kind: "sand_mixer",
      startedAt: "2026-05-24T10:25:00.000Z",
      endedAt: "2026-05-24T10:40:00.000Z",
      parameters: [
        { name: "moisture_content", value: 3.92, unit: "%", inSpec: true, range: [3.0, 4.2] },
        { name: "compactibility", value: 42, unit: "%", inSpec: true, range: [35, 50] },
        { name: "sand_temperature", value: 34, unit: "°C", inSpec: true, range: [25, 45] },
      ],
    },
    {
      id: "s-3",
      name: "Induction furnace",
      kind: "furnace",
      startedAt: "2026-05-24T10:40:00.000Z",
      endedAt: "2026-05-24T11:25:00.000Z",
      parameters: [
        { name: "melt_temperature", value: 1448, unit: "°C", inSpec: true, range: [1400, 1500] },
        { name: "power_draw", value: 415, unit: "kW", inSpec: true, range: [350, 480] },
        { name: "hold_time", value: 15, unit: "min", inSpec: true, range: [8, 25] },
        { name: "superheat", value: 88, unit: "Δ°C", inSpec: true, range: [60, 110] },
      ],
    },
    {
      id: "s-4",
      name: "OES spectrometer",
      kind: "instrument",
      startedAt: "2026-05-24T11:25:00.000Z",
      endedAt: "2026-05-24T11:28:00.000Z",
      parameters: [
        { name: "carbon", value: 3.12, unit: "%", inSpec: true, range: [3.0, 3.3] },
        { name: "silicon", value: 2.05, unit: "%", inSpec: true, range: [1.8, 2.3] },
        { name: "sulfur", value: 0.022, unit: "%", inSpec: true, range: [0.01, 0.05] },
        { name: "carbon_equivalent", value: 4.1, unit: "%", inSpec: true, range: [3.9, 4.3] },
      ],
    },
    {
      id: "s-5",
      name: "Moulding line (DISA)",
      kind: "moulding",
      startedAt: "2026-05-24T11:25:00.000Z",
      endedAt: "2026-05-24T11:45:00.000Z",
      parameters: [
        { name: "squeeze_pressure", value: 8.1, unit: "bar", inSpec: true, range: [7, 9] },
        { name: "mould_hardness", value: 82, unit: "AFS", inSpec: true, range: [70, 95] },
        { name: "mould_count", value: 102, unit: "count", inSpec: true },
      ],
    },
    {
      id: "s-6",
      name: "Pour station",
      kind: "pour",
      startedAt: "2026-05-24T11:30:00.000Z",
      endedAt: "2026-05-24T11:50:00.000Z",
      parameters: [
        { name: "pour_temperature", value: 1388, unit: "°C", inSpec: true, range: [1370, 1410] },
        { name: "pour_speed", value: 2.5, unit: "kg/s", inSpec: true, range: [1.5, 3.5] },
        { name: "inoculant_addition", value: 0.28, unit: "%", inSpec: true, range: [0.15, 0.4] },
      ],
    },
    {
      id: "s-7",
      name: "Cooling + shakeout",
      kind: "cooling",
      startedAt: "2026-05-24T11:50:00.000Z",
      endedAt: "2026-05-24T12:00:00.000Z",
      parameters: [
        { name: "cooling_time", value: 45, unit: "min", inSpec: true, range: [35, 55] },
      ],
    },
    {
      id: "s-8",
      name: "QC inspection",
      kind: "qc",
      startedAt: "2026-05-24T12:00:00.000Z",
      endedAt: "2026-05-24T12:05:00.000Z",
      parameters: [
        { name: "defect_count", value: 4, unit: "count", inSpec: true },
        { name: "rejection_rate", value: 3.9, unit: "%", inSpec: true },
      ],
    },
  ],
};

export const FX_BATCH_TRACE_FAIL: BatchTraceData = {
  heatNumber: "H-04820",
  grade: "FG200",
  shift: "B",
  startedAt: "2026-05-24T08:45:00.000Z",
  endedAt: "2026-05-24T10:35:00.000Z",
  status: "fail",
  castingsTotal: 96,
  castingsAccepted: 70,
  castingsRejected: 26,
  defects: { porosity: 18, blow_hole: 5, sand_inclusion: 3 },
  stages: [
    {
      id: "s-1",
      name: "Sand mixing",
      kind: "sand_mixer",
      startedAt: "2026-05-24T08:45:00.000Z",
      endedAt: "2026-05-24T09:00:00.000Z",
      parameters: [
        { name: "moisture_content", value: 4.45, unit: "%", inSpec: false, range: [3.0, 4.2] },
        { name: "compactibility", value: 47, unit: "%", inSpec: true, range: [35, 50] },
      ],
    },
    {
      id: "s-2",
      name: "Induction furnace",
      kind: "furnace",
      startedAt: "2026-05-24T09:00:00.000Z",
      endedAt: "2026-05-24T09:45:00.000Z",
      parameters: [
        { name: "melt_temperature", value: 1455, unit: "°C", inSpec: true, range: [1400, 1500] },
        { name: "hold_time", value: 18, unit: "min", inSpec: true, range: [8, 25] },
      ],
    },
    {
      id: "s-3",
      name: "Pour station",
      kind: "pour",
      startedAt: "2026-05-24T09:45:00.000Z",
      endedAt: "2026-05-24T10:10:00.000Z",
      parameters: [
        { name: "pour_temperature", value: 1391, unit: "°C", inSpec: true, range: [1370, 1410] },
        { name: "pour_speed", value: 2.8, unit: "kg/s", inSpec: true, range: [1.5, 3.5] },
      ],
    },
    {
      id: "s-4",
      name: "QC inspection",
      kind: "qc",
      startedAt: "2026-05-24T10:25:00.000Z",
      endedAt: "2026-05-24T10:35:00.000Z",
      parameters: [
        { name: "defect_count", value: 26, unit: "count", inSpec: false },
        { name: "rejection_rate", value: 27.1, unit: "%", inSpec: false, range: [0, 10] },
      ],
    },
  ],
};

export function getBatchTrace(heatNumber: string): BatchTraceData {
  // For Phase-0 demo: return one of two canned traces based on pass/fail.
  const row = FX_BATCHES.find((b) => b.heatNumber === heatNumber);
  if (row?.status === "fail") {
    return { ...FX_BATCH_TRACE_FAIL, heatNumber, grade: row.grade, shift: row.shift, startedAt: row.startedAt };
  }
  return { ...FX_BATCH_TRACE_PASS, heatNumber, grade: row?.grade ?? "FG260", shift: row?.shift ?? "B", startedAt: row?.startedAt ?? FX_BATCH_TRACE_PASS.startedAt };
}

// --- Defect Pareto fixture --------------------------------------------

export const FX_DEFECT_PARETO: DefectCount[] = [
  { name: "porosity", count: 142 },
  { name: "shrinkage", count: 87 },
  { name: "sand_inclusion", count: 54 },
  { name: "misrun", count: 38 },
  { name: "blow_hole", count: 28 },
  { name: "scab", count: 18 },
  { name: "hot_tear", count: 12 },
  { name: "cold_shut", count: 8 },
];

// --- Copilot canned messages ------------------------------------------

/**
 * Apply payload — when a user clicks Accept on a proposal, this drives
 * the plant-store mutation. Phase 0: canned templates referenced by id.
 * Phase 4: the LLM returns this payload as a tool call.
 */
/**
 * ProposalApply — discriminated union of every write the copilot can
 * propose. Originally three variants (add_entity, add_connection,
 * add_computed_tag); B0.43 extends to thirteen so the full copilot
 * tool registry (dashboards + alarms + tags) can flow through the
 * same proposal-card pipeline.
 *
 * Every variant's payload mirrors the corresponding
 * `CopilotTool<Payload>` payload in `lib/copilot/tools.ts` — the chat
 * UI takes a `ProposalApply`, looks up the matching tool by `type`,
 * and dispatches through `validateTool` / `applyTool`.
 */
export type ProposalApply =
  | { type: "add_entity"; templateId: string; instanceName: string }
  | {
      type: "add_connection";
      source: string;
      target: string;
      sourceHandle: string;
      targetHandle: string;
      label: string;
      materialClass: string;
    }
  | {
      // B0.37 — copilot proposes a new computed tag with a complete spec.
      // The Accept handler mints it via ComputedTagsProvider.add(). Fields
      // mirror the ComputedTagDef contract in lib/compute/types.ts; kept
      // serialisable here so the fixture corpus + the wire format match.
      type: "add_computed_tag";
      def: {
        name: string;
        description?: string;
        outputUnit?: string;
        outputDataKind: string;
        inputs: Array<{ tagId: string; alias: string }>;
        expression: string;
        trigger:
          | { kind: "on_input_change"; debounceMs?: number }
          | { kind: "scheduled"; periodSeconds: number };
        presetId?: string;
      };
    }
  // ── B0.43 dashboard-authoring tools ────────────────────────────────
  | {
      type: "create_dashboard";
      name: string;
      description?: string;
      initialWidgets?: Array<unknown>;
    }
  | {
      type: "clone_dashboard";
      sourceId: string;
      name?: string;
    }
  | {
      type: "add_widget";
      dashboardId: string;
      widget: {
        id?: string;
        type: string;
        title?: string;
        subtitle?: string;
        layout?: { x: number; y: number; w: number; h: number };
        dataSource?: { kind: "tag" | "port"; alias: string; slots: Record<string, string[]> };
        config?: Record<string, unknown>;
      };
    }
  | {
      type: "bind_widget_slot";
      dashboardId: string;
      widgetId: string;
      slotId: string;
      keys: string[];
      alias: string;
      kind?: "tag" | "port";
    }
  | {
      type: "set_widget_title";
      dashboardId: string;
      widgetId: string;
      title: string;
      subtitle?: string;
    }
  | {
      type: "delete_widget";
      dashboardId: string;
      widgetId: string;
    }
  // ── B0.43 alarms tools ─────────────────────────────────────────────
  | {
      type: "create_alarm_rule";
      name: string;
      targetTagId: string;
      targetLabel?: string;
      condition: {
        op: "gt" | "lt" | "gte" | "lte" | "eq" | "ne";
        value: number;
        forSeconds?: number;
      };
      severity?: "info" | "warning" | "critical";
      channels?: string[];
      enabled?: boolean;
    }
  | {
      type: "update_alarm_rule";
      id: string;
      patch: Record<string, unknown>;
    }
  | {
      type: "delete_alarm_rule";
      id: string;
    }
  // ── B0.43 tag patch ────────────────────────────────────────────────
  | {
      type: "patch_tag";
      id: string;
      patch: {
        description?: string;
        unit?: string;
        min_value?: number;
        max_value?: number;
        data_kind?: string;
        semantic_tag?: string;
      };
    };

export interface CopilotMessage {
  id: string;
  role: "user" | "assistant";
  content?: string;
  timestamp: string;
  proposals?: Array<{
    id: string;
    kind: "entity" | "connection" | "correlation_rule" | "clarifying_question";
    title: string;
    rationale?: string;
    citations?: Array<{ type: string; ref: string; excerpt?: string }>;
    actions: Array<"accept" | "edit" | "reject">;
    /** What to do when the user clicks Accept. */
    apply?: ProposalApply;
  }>;
}

// GATE-SCOPE3 (2026-08-03): these canned turns are the same Phase-0 assistant
// text that WS-E rewrote in `lib/copilot/context.ts:525` — restated there as a
// correlation with the handbook named as further reading, and left standing
// here because the claim gate's scope was a hand-written two-file list that
// named context.ts and not the fixture module it imports from. Stub mode is the
// only renderer today (`copilot/store.tsx:45` seeds these only when
// `isLiveApi` is false), but the string ships in every bundle, so it is held to
// the same rule rather than exempted on a build flag.
export const FX_COPILOT_MESSAGES: CopilotMessage[] = [
  {
    id: "m-1",
    role: "user",
    content:
      "We saw a porosity spike in the last shift. What changed?",
    timestamp: "2026-05-24T11:02:00.000Z",
  },
  {
    id: "m-2",
    role: "assistant",
    content:
      "Across the last shift the moisture_content from your sand mixer trended above 4.2% on 6 of 18 heats. Pearson r vs porosity rate over the last 30 days is +0.42 — that's the strongest signal in your data. That is a correlation in your own readings, not a mechanism this platform has modelled; AFS Defect Handbook §4.2 discusses the moisture–porosity relationship if you want the metallurgy.",
    timestamp: "2026-05-24T11:02:18.000Z",
    proposals: [
      {
        id: "p-1",
        kind: "correlation_rule",
        title: "Add a sand-moisture → porosity alarm",
        rationale: "Sand moisture above 4.2% predicts porosity with high confidence in your last 30 days of data.",
        citations: [
          { type: "afs", ref: "AFS Casting Defects Handbook §4.2", excerpt: "Excess moisture in green sand…" },
          { type: "tenant_data", ref: "Last 30 days · 480 heats" },
        ],
        actions: ["accept", "edit", "reject"],
      },
      {
        id: "p-2",
        kind: "clarifying_question",
        title:
          "What's the target moisture range you want me to alert against? (Default: 3.4 – 4.2%)",
        actions: ["accept", "edit"],
      },
    ],
  },
];
